import {
  ANALYTICS_NOTICE_VERSION,
  applyAnalyticsQueryFlags,
  getAppVersion,
  getBuildHash,
  getConsentState,
  isAnalyticsEnabled
} from "./analyticsConfig";
import {
  createAnalyticsId,
  getAnalyticsContext,
  getAnalyticsInstallId,
  getAnalyticsSessionId,
  nextAnalyticsSequence
} from "./analyticsContext";
import { flushAnalyticsQueue } from "./analyticsFlush";
import { enqueueAnalyticsEvent } from "./analyticsQueueRepo";
import { assertSafeAnalyticsPayload, sanitizeErrorForAnalytics } from "./analyticsRedaction";
import {
  ANALYTICS_SCHEMA_VERSION,
  type AnalyticsEvent,
  type AnalyticsEventName,
  type AnalyticsPayload,
  type AnalyticsPriority,
  type AnalyticsScreen
} from "./analyticsTypes";

let initialized = false;

export function initializeAnalyticsRuntime(): void {
  if (initialized) {
    return;
  }
  initialized = true;
  applyAnalyticsQueryFlags();

  trackAnalyticsEvent("app_open", {}, { flush: true });
  void flushAnalyticsQueue("startup");

  window.addEventListener("online", () => {
    void flushAnalyticsQueue("online");
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") {
      trackAnalyticsEvent("app_became_hidden", {}, { priority: "critical" });
      void flushAnalyticsQueue("hidden");
    } else {
      trackAnalyticsEvent("app_became_visible");
    }
  });
  window.addEventListener("error", (event) => {
    trackAnalyticsEvent(
      "runtime_error",
      sanitizeErrorForAnalytics(event.error ?? event.message, "RUNTIME_ERROR"),
      { priority: "critical", flush: true }
    );
  });
  window.addEventListener("unhandledrejection", (event) => {
    trackAnalyticsEvent(
      "unhandled_rejection",
      sanitizeErrorForAnalytics(event.reason, "UNHANDLED_REJECTION"),
      { priority: "critical", flush: true }
    );
  });
}

export function trackAnalyticsEvent(
  eventName: AnalyticsEventName,
  payload: AnalyticsPayload = {},
  options: {
    priority?: AnalyticsPriority;
    flush?: boolean;
    screen?: AnalyticsScreen;
    routePattern?: string;
  } = {}
): void {
  if (!isAnalyticsEnabled()) {
    return;
  }
  try {
    assertSafeAnalyticsPayload(payload);
    const event: AnalyticsEvent = {
      eventId: createAnalyticsId(),
      installId: getAnalyticsInstallId(),
      sessionId: getAnalyticsSessionId(),
      sequence: nextAnalyticsSequence(),
      schemaVersion: ANALYTICS_SCHEMA_VERSION,
      appVersion: getAppVersion(),
      buildHash: getBuildHash(),
      eventName,
      clientCreatedAt: new Date().toISOString(),
      analyticsEnabled: true,
      noticeVersion: ANALYTICS_NOTICE_VERSION,
      consentState: getConsentState(),
      context: getAnalyticsContext({
        screen: options.screen,
        routePattern: options.routePattern
      }),
      payload
    };
    void enqueueAnalyticsEvent(event, options.priority ?? "normal").then(() => {
      if (options.flush) {
        void flushAnalyticsQueue("milestone");
      }
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("Analytics event dropped.", error);
    }
  }
}
